import { useQuery } from '@tanstack/react-query';
import type { KnowledgeGraphResponse, KnowledgeGraphParams } from '@/types/knowledgeGraph';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

async function fetchKnowledgeGraph(params: KnowledgeGraphParams): Promise<KnowledgeGraphResponse> {
  const searchParams = new URLSearchParams({
    query: params.query,
    ...(params.limit && { limit: params.limit.toString() }),
    ...(params.depth && { depth: params.depth.toString() }),
    ...(params.max_nodes && { max_nodes: params.max_nodes.toString() }),
    ...(params.max_files_per_repo && { max_files_per_repo: params.max_files_per_repo.toString() }),
    ...(params.max_concepts_per_file && { max_concepts_per_file: params.max_concepts_per_file.toString() }),
    ...(params.include_repo_links !== undefined && { include_repo_links: params.include_repo_links.toString() }),
    ...(params.min_stars && { min_stars: params.min_stars.toString() }),
    ...(params.vector_weight && { vector_weight: params.vector_weight.toString() }),
    ...(params.concept_weight && { concept_weight: params.concept_weight.toString() }),
  });

  const response = await fetch(`${API_URL}/api/github/knowledge-graph?${searchParams}`);

  if (!response.ok) {
    throw new Error(`Knowledge graph fetch failed: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Knowledge graph generation failed');
  }

  return result.data;
}

export function useKnowledgeGraph(params: KnowledgeGraphParams) {
  return useQuery({
    queryKey: ['knowledge-graph', params],
    queryFn: () => fetchKnowledgeGraph(params),
    enabled: !!params.query, // Only fetch if query is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
